import { FC, useState } from 'react'
import { useFormik } from 'formik'
import { useQueryClient } from 'react-query'
import * as Yup from 'yup'
import {
  ButtonType,
  QUERIES,
  applyApiFieldErrors,
  showError,
} from '../../../../../_metronic/helpers'
import { TFunction, useT } from '../../../../../_metronic/i18n/Metronici18n'
import {
  Button,
  FetchState,
  FormCheckBox,
  FormDateTimeInput,
  FormInput,
  FormTextEditor,
  InfoAlert,
  LoadingIndicator,
  useModal,
  useToast,
} from '../../../../components'
import { NotaVersaoView, paraDataHoraBackend, paraDataHoraInput } from '../core/_models'
import { createNotaVersao, updateNotaVersao, uploadImagemNotaVersao } from '../core/_requests'
import { NotaVersaoCorpo } from '../components/NotaVersaoCorpo'

const MAX_VERSAO = 30
const MAX_TITULO = 200

type Valores = {
  versao: string
  titulo: string
  dtPublicacao: string
  publicada: boolean
  conteudo: string
}

const CAMPOS: (keyof Valores)[] = ['versao', 'titulo', 'dtPublicacao', 'publicada', 'conteudo']

const esquema = (t: TFunction) =>
  Yup.object({
    versao: Yup.string()
      .trim()
      .max(MAX_VERSAO, t('notas_versao.form.max.versao', { max: MAX_VERSAO }))
      .required(t('notas_versao.form.obrigatorio.versao')),
    titulo: Yup.string()
      .trim()
      .max(MAX_TITULO, t('notas_versao.form.max.titulo', { max: MAX_TITULO }))
      .required(t('notas_versao.form.obrigatorio.titulo')),
    dtPublicacao: Yup.string().required(t('notas_versao.form.obrigatorio.dt_publicacao')),
    publicada: Yup.boolean(),
    conteudo: Yup.string().required(t('notas_versao.form.obrigatorio.conteudo')),
  })

/** Falha (422 de formato/tamanho, 403, rede) vira alerta e a imagem não entra; o editor segue editável. */
const enviarImagem = (arquivo: File): Promise<string | null> =>
  uploadImagemNotaVersao(arquivo).catch((erro: unknown) => {
    showError(erro)
    return null
  })

const valoresIniciais = (nota?: NotaVersaoView): Valores => ({
  versao: nota?.versao ?? '',
  titulo: nota?.titulo ?? '',
  dtPublicacao: paraDataHoraInput(nota?.dtPublicacao),
  publicada: nota?.publicada ?? false,
  conteudo: nota?.conteudo ?? '',
})

type Props = {
  /** Ausente: criação. Presente: edição, com o cadastro vindo do fetcher `nota` do modal. */
  id?: number
}

export const NotaVersaoFormModal: FC<Props> = ({ id }) => {
  const { t } = useT()
  const { fetchStates } = useModal()

  if (!id) return <Formulario t={t} />

  const { data: nota, isLoading, error } = (fetchStates['nota'] || {}) as FetchState<NotaVersaoView>
  // O CustomModal já mostra o spinner enquanto o fetcher `nota` carrega.
  if (isLoading) return null
  if (error || !nota) {
    return (
      <InfoAlert variant='danger' role='alert'>
        {t('notas_versao.form.erro_carregar')}
      </InfoAlert>
    )
  }
  return <Formulario t={t} nota={nota} />
}

const Formulario: FC<{ t: TFunction; nota?: NotaVersaoView }> = ({ t, nota }) => {
  const { closeModal } = useModal()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [previa, setPrevia] = useState(false)

  const formik = useFormik<Valores>({
    initialValues: valoresIniciais(nota),
    validationSchema: esquema(t),
    onSubmit: async (valores, { setFieldError }) => {
      const dto = {
        ...valores,
        versao: valores.versao.trim(),
        titulo: valores.titulo.trim(),
        dtPublicacao: paraDataHoraBackend(valores.dtPublicacao),
      }
      try {
        if (nota) await updateNotaVersao(nota.id, dto)
        else await createNotaVersao(dto)
        await queryClient.invalidateQueries(QUERIES.NOTAS_VERSAO_LIST)
        // Quem publica já está logado: a nota vigente nova abre para ele também.
        queryClient.invalidateQueries(QUERIES.NOTAS_VERSAO_VIGENTE)
        showToast({
          variant: 'success',
          title: t(nota ? 'notas_versao.sucesso.atualizada' : 'notas_versao.sucesso.criada'),
        })
        closeModal()
      } catch (erro) {
        // Erro por campo vai para o campo; o resto (403, 500, campo não mapeado) vira alerta.
        setPrevia(false)
        if (applyApiFieldErrors(erro, setFieldError, CAMPOS)) showError(erro)
      }
    },
  })

  const { versao, titulo, dtPublicacao, conteudo } = formik.values
  const podePreVisualizar = Boolean(titulo.trim() && conteudo)

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      {previa ? (
        <div className='d-flex flex-column gap-4'>
          <InfoAlert variant='info' role='note'>
            {t('notas_versao.form.previa_aviso')}
          </InfoAlert>
          <NotaVersaoCorpo
            titulo={titulo}
            versao={versao}
            dtPublicacao={paraDataHoraBackend(dtPublicacao)}
            conteudo={conteudo}
          />
        </div>
      ) : (
        <>
          <div className='row'>
            <FormInput
              formik={formik}
              label={t('notas_versao.form.versao')}
              name='versao'
              placeholder={t('notas_versao.form.versao_placeholder')}
              className='col-md-4 mb-3'
              isRequired
            />
            <FormInput
              formik={formik}
              label={t('notas_versao.form.titulo')}
              name='titulo'
              placeholder={t('notas_versao.form.titulo_placeholder')}
              className='col-md-8 mb-3'
              isRequired
            />
          </div>
          <div className='row align-items-end'>
            <FormDateTimeInput
              formik={formik}
              label={t('notas_versao.form.dt_publicacao')}
              name='dtPublicacao'
              infoText={t('notas_versao.form.dt_publicacao_info')}
              className='col-md-6 mb-3'
              isRequired
            />
            <FormCheckBox
              formik={formik}
              name='publicada'
              id='nota-versao-publicada'
              label={t('notas_versao.form.publicada')}
              className='col-md-6 mb-3'
            />
          </div>
          <FormTextEditor
            formik={formik}
            label={t('notas_versao.form.conteudo')}
            name='conteudo'
            placeholder={t('notas_versao.form.conteudo_placeholder')}
            className='mb-3'
            height={300}
            isRequired
            onUploadImage={enviarImagem}
          />
        </>
      )}

      <div className='d-flex flex-wrap justify-content-end gap-2 pt-5'>
        <Button
          variant='tertiary'
          onClick={closeModal}
          disabled={formik.isSubmitting}
          type={ButtonType.Button}
        >
          {t('notas_versao.form.cancelar')}
        </Button>
        <Button
          variant='secondary-outline'
          onClick={() => setPrevia((atual) => !atual)}
          disabled={formik.isSubmitting || (!previa && !podePreVisualizar)}
          title={
            !previa && !podePreVisualizar ? t('notas_versao.form.previa_sem_conteudo') : undefined
          }
          ariaPressed={previa}
        >
          {previa ? t('notas_versao.form.voltar_edicao') : t('notas_versao.form.pre_visualizar')}
        </Button>
        <Button className='btn-primary' type={ButtonType.Submit} disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('notas_versao.form.salvar')}
        </Button>
      </div>
    </form>
  )
}
